/**
 * Country utilities with i18n support
 *
 * Provides translated country names and dropdown options
 * using the i18n-iso-countries library which includes
 * official country names in multiple languages.
 */
import countries from "i18n-iso-countries";
// Import and register locale files
import de from "i18n-iso-countries/langs/de.json";
import en from "i18n-iso-countries/langs/en.json";
import fr from "i18n-iso-countries/langs/fr.json";
import it from "i18n-iso-countries/langs/it.json";

// Register all supported locales
countries.registerLocale(en);
countries.registerLocale(fr);
countries.registerLocale(de);
countries.registerLocale(it);

/**
 * Supported locale codes
 */
export type CountryLocale = "en" | "fr" | "de" | "it";

/**
 * Dropdown option format
 */
export interface CountryOption {
  value: string;
  label: string;
  disabled?: boolean;
}

/**
 * Get all country options in the specified locale, sorted alphabetically
 *
 * @param locale - The locale to use for country names (en, fr, de, it)
 * @returns Array of country options sorted by label
 */
export function getCountryOptions(locale: CountryLocale): CountryOption[] {
  const names = countries.getNames(locale, { select: "official" });

  return Object.entries(names)
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, locale));
}

/**
 * Get country options with favorites at the top, followed by a separator,
 * then all remaining countries sorted alphabetically
 *
 * @param locale - The locale to use for country names (en, fr, de, it)
 * @param favoriteCodes - Array of country codes to place at the top (e.g., ["CH", "DE", "FR"])
 * @returns Array of country options with favorites, separator, and remaining countries
 */
export function getFavoriteCountryOptions(
  locale: CountryLocale,
  favoriteCodes: string[] = []
): CountryOption[] {
  const allCountries = getCountryOptions(locale);

  // No favorites? Return all countries
  if (favoriteCodes.length === 0) {
    return allCountries;
  }

  // Separate favorites and remaining countries
  const favorites = favoriteCodes
    .map(code => allCountries.find(country => country.value === code))
    .filter((country): country is CountryOption => country !== undefined);

  const remaining = allCountries.filter(
    country => !favoriteCodes.includes(country.value)
  );

  // Return favorites + separator + remaining
  return [
    ...favorites,
    { value: "separator", label: "──────────", disabled: true },
    ...remaining
  ];
}

/**
 * Get the translated name for a specific country code
 *
 * @param code - ISO 3166-1 alpha-2 country code (e.g., "CH", "FR", "DE")
 * @param locale - The locale to use for the country name (en, fr, de, it)
 * @returns The translated country name, or the code if not found
 */
export function getCountryName(code: string, locale: CountryLocale): string {
  return countries.getName(code, locale, { select: "official" }) || code;
}

/**
 * Get all available country codes
 *
 * @returns Array of all ISO 3166-1 alpha-2 country codes
 */
export function getAllCountryCodes(): string[] {
  return Object.keys(countries.getAlpha2Codes());
}

/**
 * Validate if a country code is valid
 *
 * @param code - The country code to validate
 * @returns True if the code is a valid ISO 3166-1 alpha-2 country code
 */
export function isValidCountryCode(code: string): boolean {
  return countries.isValid(code);
}
